import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  DownloadIcon,
  DocumentTextIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { CURRENCY_MAP } from "../../constants";
import { getOrderById } from "../../api/ordersApi";

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [toast, setToast] = useState(null);

  const [expandedItems, setExpandedItems] = useState({});

  const toggleItem = (id) =>
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency || "€";
    return `${symbol} ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getOrderById(id);

      setOrder(data);
    } catch (err) {
      setError("Failed to load order.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  if (!order) {
    return (
      <div className="p-8 text-center text-slate-400">No order data found.</div>
    );
  }

  const statusStyles = {
    completed: "bg-emerald-500/10 text-emerald-400",
    pending: "bg-amber-500/10 text-amber-400",
    failed: "bg-red-500/10 text-red-400",
    refunded: "bg-slate-500/10 text-slate-400",
  };

  const statusClass =
    statusStyles[(order.status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  const ticketStatusClass = (status) =>
    statusStyles[(status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  const totalCommission = (order.items || []).reduce(
    (sum, item) => sum + parseFloat(item.commission_amount || 0),
    0,
  );

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to orders
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-white">
            Order #{order.id}
          </h1>
        </div>
      </div>

      {/* ── All info cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Customer — col 1, spans 2 rows */}
        <div className="md:row-span-2 bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-3">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Customer
          </p>
          <InfoRow label="Full name" value={order.full_name} />
          <InfoRow label="Phone" value={order.phone} />
          <InfoRow label="Email" value={order.email} />
          <InfoRow
            label="Location"
            value={`${order.billing_town}, ${order.billing_country}`}
          />
        </div>

        {/* Order summary — col 2 */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-3">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Order summary
          </p>
          <StatRow
            label="Total orders to date"
            value={order.customer_total_orders ?? "-"}
          />
          <StatRow
            label="Total tickets to date"
            value={order.customer_total_tickets ?? "-"}
          />
          <StatRow
            label="Total value to date"
            value={formatCurrency(order.customer_total_value, order.currency)}
          />
        </div>

        {/* Order info — col 3 */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-3">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Order info
          </p>
          <StatRow label="ID" value={`#${order.order_number}`} />
          <StatRow label="Date" value={formatDate(order.created_at)} />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Payment</span>
            <span className="px-2.5 py-0.5 rounded-md text-xs font-medium bg-indigo-500/10 text-indigo-400">
              {order.payment_provider || "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Status</span>
            <span
              className={`px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${statusClass}`}
            >
              {order.status || "—"}
            </span>
          </div>
        </div>

        {/* Stripe — col 4 */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-4">
            Stripe — Payment & Fees
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Stripe Fee</span>
              <span className="text-sm text-slate-300">
                {order.stripe_fee || "-"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Currency</span>
              <span className="text-sm text-slate-300">
                {order.stripe_fee_currency || "-"}
              </span>
            </div>
            <div className="pt-3">
              <div className="text-sm text-slate-400">Transaction Id -</div>
              <span className="text-xs text-slate-300">
                {order.stripe_transaction_id || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Smartbill — spans cols 2, 3, 4 (last 3 columns) */}
        <div className="md:col-span-3 bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Smartbill — Invoice Status
            </p>
            {order.smartbill?.pdf_url && (
              <button
                onClick={() => window.open(order.smartbill.pdf_url, "_blank")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-white/10 text-xs text-slate-300 hover:text-white hover:bg-slate-700 transition"
              >
                <DocumentTextIcon className="w-4 h-4" />
                PDF
              </button>
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize ${ticketStatusClass(order.smartbill?.status)}`}
            >
              {order.smartbill?.status || "—"}
            </span>
            {order.smartbill != null && (
              <>
                <div>
                  <span className="text-sm text-slate-400">• Invoice Id: </span>
                  <span className="text-sm text-slate-300">
                    {order.smartbill?.invoice_id || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-slate-400">• Amount: </span>
                  <span className="text-sm text-slate-300">
                    {formatCurrency(
                      order.smartbill?.amount,
                      order.smartbill?.currency,
                    ) || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-slate-400">• VAT: </span>
                  <span className="text-sm text-slate-300">
                    {formatCurrency(
                      order.smartbill?.vat_amount,
                      order.smartbill?.currency,
                    ) || "-"}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Tickets table ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-sm font-semibold text-white">Tickets / Tables</h2>
          <span className="text-xs text-slate-500">
            {(order.items || []).length} item
            {(order.items || []).length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[860px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-3 text-center">Ticket Type</th>
                <th className="px-6 py-3 text-center">Ticket status</th>
                <th className="px-6 py-3 text-center">QTY</th>
                <th className="px-6 py-3 text-center">Unit price</th>
                <th className="px-6 py-3 text-center">Sub Total</th>
                <th className="px-6 py-3 text-center">
                  Commission (VAT incl.)
                </th>
                <th className="px-6 py-3 text-center">Total</th>
                <th className="px-6 py-3 text-center">PDF</th>
              </tr>
            </thead>

            <tbody>
              {(order.items || []).map((item) => (
                <>
                  <tr
                    key={item.id}
                    className="border-b border-white/5 hover:bg-slate-800/40 transition"
                  >
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {/* Expand toggle — only show if sub-tickets exist */}
                        {item.tickets?.length > 0 && (
                          <button
                            onClick={() => toggleItem(item.id)}
                            className="p-0.5 rounded text-slate-500 hover:text-white transition"
                          >
                            <ChevronDownIcon
                              className={`w-4 h-4 transition-transform ${expandedItems[item.id] ? "rotate-180" : ""}`}
                            />
                          </button>
                        )}
                        <p className="text-sm font-medium text-white">
                          {item.ticket_type?.name || "-"}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span
                        className={`px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${ticketStatusClass(item.status)}`}
                      >
                        {item.status || "—"}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-center text-sm text-slate-300">
                      {item.quantity ?? "-"}
                    </td>

                    <td className="px-6 py-4 text-center text-sm text-slate-300">
                      {formatCurrency(item.unit_price, order.currency)}
                    </td>

                    <td className="px-6 py-4 text-center text-sm text-slate-300">
                      {item.total_price ?? "-"}
                    </td>

                    <td className="px-6 py-4 text-center text-sm text-slate-300">
                      {formatCurrency(
                        item.commission_amount_vat_incl,
                        order.currency,
                      )}
                    </td>

                    <td className="px-6 py-4 text-center text-sm font-medium text-white">
                      {formatCurrency(
                        item.total_price_with_commission,
                        order.currency,
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {item.pdf_ticket ? (
                        <button
                          onClick={() => window.open(item.pdf_ticket, "_blank")}
                          className="p-1.5 rounded-md text-slate-400 hover:text-pink-400 hover:bg-pink-500/10 transition"
                          title="Download PDF"
                        >
                          <DownloadIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                  {/* Sub-rows */}
                  {expandedItems[item.id] &&
                    (item.tickets || []).map((ticket, idx) => (
                      <tr
                        key={`${item.id}-ticket-${idx}`}
                        className="border-b border-white/5 bg-slate-800/20"
                      >
                        {/* Indent under ticket type column */}
                        <td className="pl-10 pr-6 py-2.5 text-center">
                          <span className="text-xs text-slate-400">
                            {ticket.reference || `Ticket ${idx + 1}`}
                          </span>
                        </td>

                        <td className="py-2.5 text-center">
                          <span className="text-xs  text-slate-400">
                            {ticket.status || "-"}
                          </span>
                        </td>

                        {/* Empty spacer cells to match column count */}
                        <td colSpan={5} />

                        {/* PDF */}
                        <td className="px-6 py-2.5 text-center" colSpan={2}>
                          {ticket.pdf_ticket ? (
                            <button
                              onClick={() =>
                                window.open(ticket.pdf_ticket, "_blank")
                              }
                              className="p-1.5 rounded-md text-slate-400 hover:text-pink-400 hover:bg-pink-500/10 transition"
                              title="Download ticket PDF"
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </>
              ))}

              {!order.items?.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-10 text-center text-slate-400 text-sm"
                  >
                    No items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm text-slate-200">{value || "—"}</span>
  </div>
);

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-slate-400">{label}</span>
    <span className="text-sm font-medium text-slate-200">{value}</span>
  </div>
);

const TotalItem = ({ label, value, highlight }) => (
  <div className="flex flex-col items-end gap-0.5">
    <span className="text-xs uppercase tracking-widest text-slate-500">
      {label}
    </span>
    <span
      className={`text-base font-semibold ${highlight ? "text-pink-400" : "text-white"}`}
    >
      {value}
    </span>
  </div>
);

export default OrderDetailPage;
